import React from 'react'
import { Grid, Typography, Button, TextField, Box } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { Save, KeyboardArrowDown, PlusOne } from '@material-ui/icons'
import i18n from 'i18next'
import { Trans } from 'react-i18next'
import { useSelector } from 'react-redux'
import { CoverPicture, ProfilePicture } from '../../pages/t/[pid]'
import { useAlert, useChangeHandler } from '../reducer'
import { mapByIds, useAuth, useFetch } from '../context/useFetch'
import { getWilayasFromTranslation } from '../common/getWilayasFromTranslation'
import { UploadDocuments } from './documents'
import { useRouter } from 'next/router'
import { byLabel } from '../../pages/job-offer/[pid]/apply'

const FetchedProfile = React.memo(() => useFetch(
  ({ fetch }) => ({
    jobs: fetch('jobs')
  }),
  () => {
    const profile = useSelector(({ profile }) => profile)
    const auth = useAuth()
    const alert = useAlert()

    if (!profile) {
      return
    }

    const submitHandler = (event) => {
      event.preventDefault()

      auth.fetch(
        'profile',
        {
          method: 'PATCH',
          body: {
            profile: {
              ...profile,
              user_attributes: profile.user
            }
          }
        }
      ).then(() => alert('notice', 'Profil enregistré avec succès'))
        .catch(() => alert('errror', 'Une erreur s\'est produite'))
    }

    return (
      <form onSubmit={submitHandler}>
        <Typography variant="h2" component="h1">
          <Trans i18nKey="profile.candidat.personal_info" />
        </Typography>

        <Box mb={10}>
          <CoverPicture path={['profile']} edit />
          <ProfilePicture path={['profile']} edit />
        </Box>

        <DocumentSection />

        <IdentitySection />

        <ContactSection />

        <LinksSection />

        <Grid className="profileSection" container justifyContent="flex-end">
          <Button type="submit" variant="contained" size="large" color="primary" startIcon={<Save />}>
            <Trans i18nKey="profile.candidat.profile_tab.save" />
          </Button>
        </Grid>
      </form>
    )
  }
))

const Profile = () => <FetchedProfile />

const IdentitySection = () => {
  const profile = useSelector(({ profile }) => profile)
  const jobs = useSelector(({ jobs }) => jobs)

  const jobsById = mapByIds(jobs)

  const handler = useChangeHandler(profile, 'profile')

  return (
    <Grid className="profileSection">
      <Typography variant="h3" component="h2">
        <Trans i18nKey="profile.candidat.profile_tab.description.identity" />
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.first_name')}
            placeholder={i18n.t('profile.candidat.profile_tab.first_name_placeholder')}
            fullWidth
            required
            InputProps={{ disableUnderline: true }}
            {...handler.default('first_name')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.last_name')}
            placeholder={i18n.t('profile.candidat.profile_tab.last_name_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('last_name')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={jobs.filter(({ parent_job_id }) => Boolean(parent_job_id))}
            getOptionLabel={(option) => option.label}
            popupIcon={<KeyboardArrowDown />}
            disableClearable
            renderInput={(params) => <TextField label={i18n.t('profile.candidat.profile_tab.jobs')} variant="filled" fullWidth required {...params} InputProps={{ ...params.InputProps, disableUnderline: true }} />}
            groupBy={(option) => option.parent_job_id && jobsById[option.parent_job_id]?.label}
            {...handler.complete('id', [null, ...jobs])('job_id')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.title')}
            placeholder={i18n.t('profile.candidat.profile_tab.title_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('title')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            type="date"
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.birthdate')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            {...handler.default('birthdate')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.description.label')}
            placeholder={i18n.t('profile.candidat.profile_tab.description.placeholder')}
            fullWidth
            multiline
            InputProps={{ disableUnderline: true }}
            {...handler.default('description')}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const ContactSection = () => {
  const profile = useSelector(({ profile }) => profile)
  const wilayas = getWilayasFromTranslation()

  const handler = useChangeHandler(profile, 'profile')

  return (
    <Grid className="profileSection">
      <Typography variant="h3" component="h2">
        <Trans i18nKey="profile.candidat.profile_tab.description.contact" />
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.mail')}
            placeholder={i18n.t('profile.candidat.profile_tab.mail_placeholder')}
            fullWidth
            required
            InputProps={{ disableUnderline: true }}
            {...handler.default('user', 'email')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.phone')}
            placeholder={i18n.t('profile.candidat.profile_tab.phone_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('phone')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.full_address')}
            placeholder={i18n.t('profile.candidat.profile_tab.full_address_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('address')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.ville')}
            placeholder={i18n.t('profile.candidat.profile_tab.ville_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('town')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={wilayas}
            getOptionLabel={(option) => option.name}
            popupIcon={<KeyboardArrowDown />}
            disableClearable
            renderInput={(params) => <TextField label={i18n.t('profile.candidat.profile_tab.wilaya')} variant="filled" fullWidth required {...params} InputProps={{ ...params.InputProps, disableUnderline: true }} />}
            {...handler.complete('code', [null, ...wilayas])('department_code')}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const LinksSection = () => {
  const profile = useSelector(({ profile }) => profile)

  const handler = useChangeHandler(profile, 'profile')

  return (
    <Grid className="profileSection">
      <Typography variant="h3" component="h2">
        <Trans i18nKey="profile.candidat.profile_tab.description.liens" />
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('dialog.linkedin')}
            placeholder={i18n.t('profile.candidat.profile_tab.linkedin_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('linkedin')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('dialog.github')}
            placeholder={i18n.t('profile.candidat.profile_tab.github_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('github')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="filled"
            label={i18n.t('profile.candidat.profile_tab.website')}
            placeholder={i18n.t('profile.candidat.profile_tab.website_placeholder')}
            fullWidth
            InputProps={{ disableUnderline: true }}
            {...handler.default('website')}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const DocumentSection = () => useFetch(
  ({ fetch }) => ({
    documentTypes: fetch('document_types')
  }),
  ({ documentTypes }) => {
    const cvDocumentType = Object.values(documentTypes).find((document) => document.slug === 'cv')
    const router = useRouter()

    return (
      <Grid className="profileSection">
        <Grid container justifyContent="space-between">
          <Typography variant="h3" component="h2">
            <Trans i18nKey="profile.candidat.documents.title" />
          </Typography>

          <Button variant="text" endIcon={<PlusOne />} color="primary" onClick={() => router.push('/profile/documents')}>
            <Trans i18nKey="profile.candidat.documents.preview" />
          </Button>
        </Grid>

        <UploadDocuments documentType={cvDocumentType} callback={() => router.push('/profile/documents')} />
      </Grid>
    )
  }
)

export default Profile
